import type {
    College,
    Department,
    Gender,
    Track,
} from "@/Components/Home/DreamUniversity/data/sharedTypes";
import type { Scores } from "@/Components/Home/DreamUniversity/utils/calculateEquivalentRate";
import type { UniversityId } from "@/Components/Home/DreamUniversity/data/universityRegistry";
import type {
    FmCountry,
    FmDiscipline,
} from "@/Components/Home/DreamUniversity/data/foreignMissionsData";
import type {
    ImInstitution,
    ImDiscipline,
} from "@/Components/Home/DreamUniversity/data/internalMissionsData";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export type DreamStepId =
    | "university"
    | "gender"
    | "fm_country"
    | "fm_college"
    | "im_institution"
    | "im_discipline"
    | "im_specialty"
    | "college"
    | "specialty"
    | "paaet_docs"
    | "scores"
    | "results";

interface DreamUniversityState {
    activeStepId: DreamStepId;
    universityId: UniversityId | null;
    gender: Gender | null;
    college: College | null;
    department: Department | null;
    track: Track | null;
    fmCountry: FmCountry | null;
    fmDiscipline: FmDiscipline | null;
    imInstitution: ImInstitution | null;
    imDiscipline: ImDiscipline | null;
    scores: Record<string, string>;
    resultScores: Scores | null;
    equivalentRate: number;
    requiredFieldsCount: number | null;
}

const initialState: DreamUniversityState = {
    activeStepId: "university",
    universityId: null,
    gender: null,
    college: null,
    department: null,
    track: null,
    fmCountry: null,
    fmDiscipline: null,
    imInstitution: null,
    imDiscipline: null,
    scores: {},
    resultScores: null,
    equivalentRate: 0,
    requiredFieldsCount: null,
};

const clearSelections = (state: DreamUniversityState): void => {
    state.gender = null;
    state.college = null;
    state.department = null;
    state.track = null;
    state.fmCountry = null;
    state.fmDiscipline = null;
    state.imInstitution = null;
    state.imDiscipline = null;
    state.scores = {};
    state.resultScores = null;
    state.equivalentRate = 0;
    state.requiredFieldsCount = null;
};

/** Reset the entered numbers only when the new selection needs a different
 *  number of score inputs than the previous one. */
const applyRequiredCountRule = (
    state: DreamUniversityState,
    requiredCount: number,
): void => {
    if (
        state.requiredFieldsCount !== null &&
        state.requiredFieldsCount !== requiredCount
    ) {
        state.scores = {};
    }
    state.requiredFieldsCount = requiredCount;
};

export const dreamUniversitySlice = createSlice({
    name: "dreamUniversity",
    initialState,
    reducers: {
        stepChanged: (state, action: PayloadAction<DreamStepId>) => {
            state.activeStepId = action.payload;
        },
        universityPicked: (state, action: PayloadAction<UniversityId>) => {
            if (state.universityId !== action.payload) {
                clearSelections(state);
                state.universityId = action.payload;
            }
        },
        genderPicked: (state, action: PayloadAction<Gender>) => {
            state.gender = action.payload;
            state.college = null;
            state.department = null;
            state.track = null;
        },
        trackChanged: (state, action: PayloadAction<Track | null>) => {
            state.track = action.payload;
        },
        fmCountryPicked: (
            state,
            action: PayloadAction<{ country: FmCountry; track: Track }>,
        ) => {
            state.fmCountry = action.payload.country;
            state.track = action.payload.track;
            state.fmDiscipline = null;
            state.college = null;
            state.department = null;
        },
        fmDisciplinePicked: (state, action: PayloadAction<FmDiscipline>) => {
            state.fmDiscipline = action.payload;
            state.college = null;
            state.department = null;
        },
        imInstitutionPicked: (
            state,
            action: PayloadAction<{ institution: ImInstitution; track: Track }>,
        ) => {
            state.imInstitution = action.payload.institution;
            state.track = action.payload.track;
            state.imDiscipline = null;
            state.college = null;
            state.department = null;
        },
        imDisciplinePicked: (state, action: PayloadAction<ImDiscipline>) => {
            state.imDiscipline = action.payload;
            state.college = null;
            state.department = null;
        },
        collegePicked: (
            state,
            action: PayloadAction<{
                college: College;
                requiredCount: number | null;
            }>,
        ) => {
            state.college = action.payload.college;
            state.department = null;
            if (action.payload.requiredCount !== null) {
                applyRequiredCountRule(state, action.payload.requiredCount);
            }
        },
        specialtyPicked: (
            state,
            action: PayloadAction<{
                department: Department;
                college?: College;
                requiredCount: number;
            }>,
        ) => {
            state.department = action.payload.department;
            if (action.payload.college) {
                state.college = action.payload.college;
            }
            applyRequiredCountRule(state, action.payload.requiredCount);
        },
        scoreFieldChanged: (
            state,
            action: PayloadAction<{ field: string; value: string }>,
        ) => {
            state.scores[action.payload.field] = action.payload.value;
        },
        resultReady: (
            state,
            action: PayloadAction<{ scores: Scores; rate: number }>,
        ) => {
            state.resultScores = action.payload.scores;
            state.equivalentRate = action.payload.rate;
        },
        selectionsHealed: (
            state,
            action: PayloadAction<{
                college: College | null;
                department: Department | null;
            }>,
        ) => {
            state.college = action.payload.college;
            state.department = action.payload.department;
        },
        flowReset: () => initialState,
    },
});

export const {
    stepChanged,
    universityPicked,
    genderPicked,
    trackChanged,
    fmCountryPicked,
    fmDisciplinePicked,
    imInstitutionPicked,
    imDisciplinePicked,
    collegePicked,
    specialtyPicked,
    scoreFieldChanged,
    resultReady,
    selectionsHealed,
    flowReset,
} = dreamUniversitySlice.actions;
